import React, { useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Stack,
  Chip,
  IconButton,
  Button,
  useMediaQuery,
  useTheme
} from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import parse from 'html-react-parser';
import './custom.css';

const Email = ({ 
  name, 
  subject, 
  body, 
  date, 
  attachments, 
  initialEmailView, 
  handleInitialEmailView, 
  showBackButton = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  
  useEffect(() => {
    // Only reset initialEmailView when leaving mobile view
    return () => {
      if (isMobile) {
        handleInitialEmailView(false);
      }
    };
  }, [handleInitialEmailView, isMobile]);

  const handleAttachmentOpen = (url) => {
    window.open(url, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!initialEmailView) {
    return null;
  }



  return (
    <Box className="email-container" sx={{ 
      width: '100%',
      maxWidth: '1000px',
      mx: 'auto',
      flexGrow: 1,
      p: isMobile ? 2 : 3
    }}>
      {showBackButton && isMobile && (
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={() => handleInitialEmailView(false)}>
            Back to Email List
          </Button>
        </Box>
      )}
      <Paper elevation={2} className="email-paper" sx={{ p: 2, borderRadius: 2 }}>
        {/* Email Header */}
        <Box className="email-header" sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 700 }}>
            {subject}
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
            <Chip 
              label={`From: ${name}`}
              variant="outlined"
              color="primary"
              size="small"
            />
            <Chip 
              label={`Sent: ${formatDate(date)}`}
              variant="outlined"
              color="default"
              size="small"
            />
          </Stack>
        </Box>

        {/* Email Body */}
        <Box className="email-body" sx={{ 
          mb: 3,
          p: 2,
          bgcolor: '#fafafa',
          borderRadius: 1,
          minHeight: '200px'
        }}>
          <Typography component="div" sx={{ 
            lineHeight: 1.6,
            '& p': { mb: 1 },
            '& img': { maxWidth: '100%', height: 'auto' }
          }}>
            {parse(body)}
          </Typography>
        </Box>

        {/* Attachments Section */}
        {attachments && attachments.length > 0 && (
          <Box className="email-attachments" sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <AttachFileIcon sx={{ mr: 1 }} />
              Attachments ({attachments.length})
            </Typography>
            
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {attachments.map((attachmentUrl, index) => (
                <Paper
                  key={index}
                  elevation={1}
                  className="attachment-item"
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '120px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      elevation: 3
                    }
                  }}
                  onClick={() => handleAttachmentOpen(attachmentUrl)}
                >
                  <Box
                    component="img"
                    src={attachmentUrl}
                    alt={`Attachment ${index + 1}`}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      mb: 1
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <Box
                    sx={{
                      display: 'none',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <AttachFileIcon color="action" />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    Attachment {index + 1}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAttachmentOpen(attachmentUrl);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Email;